import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

export interface CollapsibleProps {
  /** Section title displayed in the header */
  title: string;
  /** Optional description shown below the title */
  description?: string;
  /** Whether the section is expanded by default */
  defaultOpen?: boolean;
  /** Optional badge element to display in the header */
  badge?: React.ReactNode;
  /** Content to show when expanded */
  children: React.ReactNode;
  /** Additional classes for the container */
  className?: string;
}

/**
 * Collapsible section component with animated expand/collapse.
 * Used to organize settings into expandable groups.
 */
export function Collapsible({
  title,
  description,
  defaultOpen = true,
  badge,
  children,
  className
}: CollapsibleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={cn(
        'rounded-lg border bg-card text-card-foreground shadow-sm',
        className
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-secondary/50"
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold">{title}</span>
            {badge}
          </div>
          {description && (
            <span className="text-sm text-muted-foreground">{description}</span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-200',
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="border-t border-border/50 p-4">{children}</div>
      </div>
    </div>
  );
}
